package dev.danko.roblox_craft_ai.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.icons.MoonIcon
import com.varabyte.kobweb.silk.components.icons.SunIcon
import com.varabyte.kobweb.silk.components.icons.fa.FaIcon
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.danko.roblox_craft_ai.components.widgets.IconButton
import dev.danko.roblox_craft_ai.shared.model.AssetType
import dev.danko.roblox_craft_ai.shared.model.GenerationStatus
import dev.danko.roblox_craft_ai.uikit.components.*
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import dev.danko.roblox_craft_ai.uikit.theme.ProvideAppTheme
import kotlinx.browser.window
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

enum class DocsCategory {
    BUTTONS_INPUTS,
    CARDS_SURFACES,
    VIEWERS_PIPELINES,
    FEEDBACK_MODALS,
    DESIGN_TOKENS
}

@Page("/docs")
@Composable
fun DocsPage() {
    ProvideAppTheme {
        val theme = LocalAppTheme.current
        val palette = theme.colors
        var colorMode by ColorMode.currentState
        var selectedCategory by remember { mutableStateOf(DocsCategory.BUTTONS_INPUTS) }

        // Component Interactive States
        var inputText by remember { mutableStateOf("RobloxCraft User") }
        var inputError by remember { mutableStateOf<String?>(null) }
        var selectedOption by remember { mutableStateOf("luau") }
        var buttonLoading by remember { mutableStateOf(false) }
        var showSampleModal by remember { mutableStateOf(false) }

        val categoryTabs = listOf(
            TabItem(DocsCategory.BUTTONS_INPUTS, "Buttons & Inputs", icon = { FaIcon("box", Modifier) }),
            TabItem(DocsCategory.CARDS_SURFACES, "Surfaces & Cards", icon = { FaIcon("cubes", Modifier) }),
            TabItem(DocsCategory.VIEWERS_PIPELINES, "Viewers & Steppers", icon = { FaIcon("code", Modifier) }),
            TabItem(DocsCategory.FEEDBACK_MODALS, "Feedback & Toasts", icon = { FaIcon("book", Modifier) }),
            TabItem(DocsCategory.DESIGN_TOKENS, "Design Tokens", icon = null)
        )

        val selectOptions = listOf(
            SelectOption("luau", "Luau Script (Client/Server)", "Roblox game logic script generator"),
            SelectOption("mesh", "3D Mesh (LowPoly/Stylized)", "Tripo3D and Meshy.ai models"),
            SelectOption("anim", "R15 Rig Animation", "KeyframeSequence generator")
        )

        Column(
            modifier = Modifier
                .fillMaxSize()
                .minHeight(100.percent)
                .backgroundColor(palette.background)
                .padding(leftRight = 2.cssRem, topBottom = 1.5.cssRem)
        ) {
            // Header
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = 1.5.cssRem)
                    .borderBottom(1.px, LineStyle.Solid, palette.border),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        modifier = Modifier
                            .margin(right = 1.cssRem)
                            .padding(all = 0.5.cssRem)
                            .borderRadius(theme.radii.md)
                            .backgroundColor(palette.surfaceElevated)
                            .border(1.px, LineStyle.Solid, palette.border)
                            .cursor(Cursor.Pointer)
                            .onClick { window.location.href = "/" }
                    ) {
                        FaIcon("arrow-left", Modifier.color(palette.textPrimary))
                    }

                    Column {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            SpanText(
                                text = "RobloxCraft UI-Kit Storybook",
                                modifier = Modifier
                                    .fontSize(1.5.cssRem)
                                    .fontWeight(FontWeight.Bold)
                                    .color(palette.textPrimary)
                            )
                            SpanText(
                                text = "v0.1.0",
                                modifier = Modifier
                                    .margin(left = 0.75.cssRem)
                                    .padding(leftRight = 0.5.cssRem, topBottom = 0.15.cssRem)
                                    .borderRadius(theme.radii.full)
                                    .backgroundColor(palette.accent)
                                    .color(Color.rgb(0xFFFFFF))
                                    .fontSize(0.75.cssRem)
                                    .fontWeight(FontWeight.Bold)
                            )
                        }
                        SpanText(
                            text = "Interactive Component Library & Design System for Kobweb / Silk",
                            modifier = Modifier.fontSize(0.85.cssRem).color(palette.textSecondary)
                        )
                    }
                }

                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(0.5.cssRem)) {
                    AppButton(
                        text = "🏠 Главная",
                        variant = AppButtonVariant.OUTLINE,
                        size = AppButtonSize.SM,
                        onClick = { window.location.href = "/" }
                    )
                    AppButton(
                        text = "⚙️ Настройки",
                        variant = AppButtonVariant.OUTLINE,
                        size = AppButtonSize.SM,
                        onClick = { window.location.href = "/settings" }
                    )
                    AppButton(
                        text = "📚 Пресеты",
                        variant = AppButtonVariant.OUTLINE,
                        size = AppButtonSize.SM,
                        onClick = { window.location.href = "/prompts" }
                    )
                    IconButton(onClick = { colorMode = colorMode.opposite }) {
                        if (colorMode.isDark) SunIcon() else MoonIcon()
                    }
                }
            }

            // Quick Install Banner
            AppAlert(
                title = "Gradle Dependency",
                message = "implementation(\"dev.danko.roblox:ui-kit:0.1.0\")",
                variant = AlertVariant.INFO,
                modifier = Modifier.margin(bottom = 1.5.cssRem)
            )

            // Category Tabs
            AppTabs(
                tabs = categoryTabs,
                activeTab = selectedCategory,
                onTabSelected = { selectedCategory = it },
                modifier = Modifier.margin(bottom = 1.5.cssRem)
            )

            // Category Content
            when (selectedCategory) {
                DocsCategory.BUTTONS_INPUTS -> {
                    Column(modifier = Modifier.fillMaxWidth().gap(1.5.cssRem)) {
                        AppCard(modifier = Modifier.fillMaxWidth().padding(1.cssRem)) {
                            Column(modifier = Modifier.gap(1.cssRem)) {
                                SpanText(
                                    text = "AppButton Variants & Sizes",
                                    modifier = Modifier.fontSize(1.1.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary)
                                )
                                Row(modifier = Modifier.gap(0.75.cssRem), verticalAlignment = Alignment.CenterVertically) {
                                    AppButton(text = "Primary", variant = AppButtonVariant.PRIMARY, size = AppButtonSize.MD, onClick = {
                                        ToastManager.info("Clicked Primary Button")
                                    })
                                    AppButton(text = "Secondary", variant = AppButtonVariant.SECONDARY, size = AppButtonSize.MD, onClick = {})
                                    AppButton(text = "Outline", variant = AppButtonVariant.OUTLINE, size = AppButtonSize.MD, onClick = {})
                                    AppButton(text = "Danger", variant = AppButtonVariant.DANGER, size = AppButtonSize.MD, onClick = {})
                                    AppButton(
                                        text = if (buttonLoading) "Loading..." else "Toggle Loading",
                                        loading = buttonLoading,
                                        variant = AppButtonVariant.PRIMARY,
                                        onClick = { buttonLoading = !buttonLoading }
                                    )
                                }
                            }
                        }

                        AppCard(modifier = Modifier.fillMaxWidth().padding(1.cssRem)) {
                            Column(modifier = Modifier.gap(1.cssRem)) {
                                SpanText(
                                    text = "AppInput & Validation States",
                                    modifier = Modifier.fontSize(1.1.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary)
                                )
                                Row(modifier = Modifier.fillMaxWidth().gap(1.cssRem)) {
                                    AppInput(
                                        value = inputText,
                                        onValueChanged = {
                                            inputText = it
                                            inputError = if (it.isBlank()) "Input cannot be blank" else null
                                        },
                                        label = "Display Name",
                                        placeholder = "Enter your handle",
                                        error = inputError,
                                        helperText = if (inputError == null) "Visible to other Studio team members" else null,
                                        modifier = Modifier.weight(1f)
                                    )

                                    AppSelect(
                                        options = selectOptions,
                                        selected = selectedOption,
                                        onSelect = { selectedOption = it },
                                        label = "Asset Generator",
                                        modifier = Modifier.weight(1f)
                                    )
                                }
                            }
                        }
                    }
                }

                DocsCategory.CARDS_SURFACES -> {
                    Column(modifier = Modifier.fillMaxWidth().gap(1.5.cssRem)) {
                        AppCard(modifier = Modifier.fillMaxWidth().padding(1.cssRem)) {
                            Column(modifier = Modifier.gap(0.75.cssRem)) {
                                SpanText(
                                    text = "PromptBox Component",
                                    modifier = Modifier.fontSize(1.1.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary)
                                )
                                PromptBox(
                                    onSubmit = { prompt, type, provider, refs, _ ->
                                        ToastManager.success("Prompt submitted: $prompt ($type via $provider, ${refs.size} refs)")
                                    }
                                )
                            }
                        }
                    }
                }

                DocsCategory.VIEWERS_PIPELINES -> {
                    Column(modifier = Modifier.fillMaxWidth().gap(1.5.cssRem)) {
                        AppCard(modifier = Modifier.fillMaxWidth().padding(1.cssRem)) {
                            Column(modifier = Modifier.gap(1.cssRem)) {
                                SpanText(
                                    text = "StatusPipeline Horizontal Stepper",
                                    modifier = Modifier.fontSize(1.1.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary)
                                )
                                StatusPipeline(
                                    steps = createDefaultPipelineSteps(
                                        type = AssetType.MESH_3D,
                                        overallStatus = GenerationStatus.PROCESSING,
                                        metadata = mapOf("meshProgress" to "55")
                                    )
                                )
                            }
                        }

                        AppCard(modifier = Modifier.fillMaxWidth().padding(1.cssRem)) {
                            Column(modifier = Modifier.gap(1.cssRem)) {
                                SpanText(
                                    text = "MeshViewer (Three.js WebGL)",
                                    modifier = Modifier.fontSize(1.1.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary)
                                )
                                MeshViewer(
                                    modelUrl = "https://models.readyplayer.me/6460d36c74d45c0f9d0c72d1.glb",
                                    prompt = "Chest"
                                )
                            }
                        }

                        AppCard(modifier = Modifier.fillMaxWidth().padding(1.cssRem)) {
                            Column(modifier = Modifier.gap(1.cssRem)) {
                                SpanText(
                                    text = "CodeViewer (Syntax Highlighted Luau)",
                                    modifier = Modifier.fontSize(1.1.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary)
                                )
                                CodeViewer(
                                    code = """
                                    --!strict
                                    local ReplicatedStorage = game:GetService("ReplicatedStorage")
                                    local TweenService = game:GetService("TweenService")
                                    
                                    local function PlayBounce(part: BasePart)
                                        local tweenInfo = TweenInfo.new(0.6, Enum.EasingStyle.Bounce, Enum.EasingDirection.Out)
                                        local tween = TweenService:Create(part, tweenInfo, { Position = part.Position + Vector3.new(0, 5, 0) })
                                        tween:Play()
                                    end
                                    """.trimIndent(),
                                    language = "lua"
                                )
                            }
                        }
                    }
                }

                DocsCategory.FEEDBACK_MODALS -> {
                    Column(modifier = Modifier.fillMaxWidth().gap(1.5.cssRem)) {
                        AppCard(modifier = Modifier.fillMaxWidth().padding(1.cssRem)) {
                            Column(modifier = Modifier.gap(1.cssRem)) {
                                SpanText(
                                    text = "AppAlert Banner Variants",
                                    modifier = Modifier.fontSize(1.1.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary)
                                )
                                AppAlert(title = "Info Banner", message = "Ktor backend running on port 9095.", variant = AlertVariant.INFO)
                                AppAlert(title = "Success Banner", message = "Asset successfully synchronized to Roblox Studio!", variant = AlertVariant.SUCCESS)
                                AppAlert(title = "Warning Banner", message = "Tripo3D API key will expire in 3 days.", variant = AlertVariant.WARNING)
                                AppAlert(title = "Danger Banner", message = "Syntax validation error on Line 12: unbalanced 'end'.", variant = AlertVariant.DANGER)
                            }
                        }

                        AppCard(modifier = Modifier.fillMaxWidth().padding(1.cssRem)) {
                            Column(modifier = Modifier.gap(1.cssRem)) {
                                SpanText(
                                    text = "Toast Notification Triggers",
                                    modifier = Modifier.fontSize(1.1.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary)
                                )
                                Row(modifier = Modifier.gap(0.75.cssRem)) {
                                    AppButton("Trigger Info Toast", variant = AppButtonVariant.SECONDARY, onClick = {
                                        ToastManager.info("This is an info toast notification.")
                                    })
                                    AppButton("Trigger Success Toast", variant = AppButtonVariant.PRIMARY, onClick = {
                                        ToastManager.success("Operation completed successfully!")
                                    })
                                    AppButton("Trigger Warning Toast", variant = AppButtonVariant.SECONDARY, onClick = {
                                        ToastManager.warning("High latency detected on LLM endpoint.")
                                    })
                                    AppButton("Trigger Error Toast", variant = AppButtonVariant.DANGER, onClick = {
                                        ToastManager.error("Failed to connect to Roblox Studio plugin.")
                                    })
                                }
                            }
                        }

                        AppCard(modifier = Modifier.fillMaxWidth().padding(1.cssRem)) {
                            Column(modifier = Modifier.gap(1.cssRem)) {
                                SpanText(
                                    text = "Modal Dialog Trigger",
                                    modifier = Modifier.fontSize(1.1.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary)
                                )
                                AppButton("Open Sample Modal", variant = AppButtonVariant.PRIMARY, onClick = {
                                    showSampleModal = true
                                })
                            }
                        }
                    }
                }

                DocsCategory.DESIGN_TOKENS -> {
                    Column(modifier = Modifier.fillMaxWidth().gap(1.5.cssRem)) {
                        AppCard(modifier = Modifier.fillMaxWidth().padding(1.cssRem)) {
                            Column(modifier = Modifier.gap(1.cssRem)) {
                                SpanText(
                                    text = "Semantic Color Tokens",
                                    modifier = Modifier.fontSize(1.1.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary)
                                )
                                Row(modifier = Modifier.fillMaxWidth().gap(1.cssRem)) {
                                    listOf(
                                        "Accent" to palette.accent,
                                        "Surface" to palette.surface,
                                        "Elevated" to palette.surfaceElevated,
                                        "Pending" to palette.statusPending,
                                        "Processing" to palette.statusProcessing,
                                        "Ready" to palette.statusReady,
                                        "Failed" to palette.statusFailed
                                    ).forEach { (name, col) ->
                                        Column(
                                            modifier = Modifier
                                                .weight(1f)
                                                .padding(0.75.cssRem)
                                                .backgroundColor(col)
                                                .borderRadius(theme.radii.md)
                                                .border(1.px, LineStyle.Solid, palette.border),
                                            horizontalAlignment = Alignment.CenterHorizontally
                                        ) {
                                            SpanText(
                                                text = name,
                                                modifier = Modifier.fontSize(0.8.cssRem).fontWeight(FontWeight.Bold).color(Color.rgb(0xFFFFFF))
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Sample Modal
            ModalDialog(
                visible = showSampleModal,
                onCloseRequest = { showSampleModal = false },
                title = "UI-Kit Sample Modal",
                footer = {
                    AppButton(
                        text = "Got it!",
                        variant = AppButtonVariant.PRIMARY,
                        onClick = {
                            showSampleModal = false
                            ToastManager.success("Confirmed from sample modal!")
                        }
                    )
                }
            ) {
                Column(modifier = Modifier.gap(0.5.cssRem)) {
                    SpanText("This modal is powered by the unified ModalDialog primitive from :ui-kit.")
                    SpanText("It supports customizable titles, action buttons, full keyboard accessibility, and backdrops.")
                }
            }

            ToastContainer()
        }
    }
}
